<template>
  <div>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            icon
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="modal-style">
          <h3>{{ $t("components.OperatorsLogs.question2.title") }}</h3>
          <p>{{ $t("components.OperatorsLogs.question2.description") }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <GenericChart
      :request-status="requestStatus"
      :title="title"
      :items="items"
      :map-keys="mapKeys"
      :item-id="itemId"
      :open-dialog="openDialog"
      class="make-a-copy"
      v-bind="$attrs"
      @reload="getData"
    />

    <v-data-table
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      sort-by="long_posted_at"
      :sort-asc="true"
      class="table-truncate"
    >
      <template v-slot:item.seeConversation="{ item }">
        <DsButton
          v-tooltip="openChatLabel"
          class="clean-sla-buttons"
          color="ghost"
          name="chat"
          :icon="true"
          @click="encodeFilter(item.sac_call_id)"
        />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import GenericChart from '@/components/cards/models/GenericChart';
import sacTypeModel from '@/mixins/sac-type-model';
import channelModel from '@/mixins/channel-model';
import statusModel from '@/mixins/status-model';
import EventBus from '@/utils/event-bus';
import { mapActions, mapGetters, mapState } from 'vuex';
import { DsButton } from '@ds/components';
import WarRoomService from '@/services/WarRoomService';
import { PROJECTION_OPTIONS, SORT_OPTIONS } from '@/utils/constants.js';
import { getTodayInNormalizedPattern, calculateTimeSincePosted } from '@/utils/date.js';
import { getWarroomURL, getSmartCareURL } from '@/utils/url.js';

export default {
  name: 'BacklogTable',
  components: {
    GenericChart,
    DsButton,
  },
  mixins: [
    sacTypeModel,
    channelModel,
    statusModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      rawItems: [],
      dialog: false,
      itemsPerPage: 5,
      requestStatus: 'loading',
      initialDate: '202212010000',
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('components.OperatorsLogs.sacCallId'), value: 'sac_call_id' },
        { text: this.$t('components.OperatorsLogs.channel'), value: 'channel' },
        { text: this.$t('components.OperatorsLogs.status'), value: 'status' },
        { text: this.$t('components.OperatorsLogs.date'), value: 'formatted_posted_at' },
        { text: this.$t('components.OperatorsLogs.smartcareDate'), value: 'smartcare_update_date' },
        { text: this.$t('components.OperatorsLogs.sla'), value: 'time_since_posted' },
        { text: this.$t('components.OperatorsLogs.smartcareUpdateDate'), value: 'time_since_posted_update' },
        { text: this.$t('components.OperatorsLogs.seeConversation'), value: 'seeConversation' },
      ];
    },
    infoLabel() {
      return this.$t('components.OperatorsLogs.infoLabel');
    },
    closeLabel() {
      return this.$t('components.OperatorsLogs.closeLabel');
    },
    openChatLabel() {
      return this.$t('components.OperatorsLogs.openChatLabel');
    },
    mapKeys() {
      return {
        sac_call_id: this.$t('components.OperatorsLogs.sacCallId'),
        channel: this.$t('components.OperatorsLogs.channel'),
        status: this.$t('components.OperatorsLogs.status'),
        formatted_posted_at: this.$t('components.OperatorsLogs.date'),
        time_since_posted: this.$t('components.OperatorsLogs.sla'),
        time_since_posted_update: this.$t('components.OperatorsLogs.smartcareUpdateDate'),
        smartcare_update_date: this.$t('components.OperatorsLogs.smartcareDate'),
      };
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
      'activeDashboard',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    ...mapGetters('filter', [
      'formattedDateRange',
    ]),
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    getOptions() {
      return this.SAC_TYPE_MAP;
    },
    standardDateRange() {
      const endingAt = getTodayInNormalizedPattern();

      return `${this.initialDate}:${endingAt}`;
    },
    items() {
      return this.rawItems.map(post => ({
        ...post,
        channel: this.getChannelColorNameAndIcon(post.raw_channel).name,
        status: this.getStatusColorAndName(post.raw_status).name,
        time_since_posted: this.formatTimeSincePosted(post.calculated_time),
        time_since_posted_update: this.formatTimeSincePosted(post.calculated_time_update),
      }));
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getSeStudioEndpointService',
      'pages',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.applyFilter(options);
    },
    applyFilter(options) {
      this.requestStatus = 'loading';
      this.rawItems = [];
      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'backlogTable',
        serviceParams: {
          date_range: this.formattedDateRange,
          limit: 5000,
          offset: 0,
          sort: true,
          sort_options: SORT_OPTIONS.postedAt,
          projection: PROJECTION_OPTIONS.teamPerformance,
        },
      })
        .then((response) => {
          this.rawItems = response.posts.map(post => ({
            ...post,
            raw_channel: post.channel,
            raw_status: post.status,
            calculated_time: calculateTimeSincePosted(post.long_posted_at),
            formatted_posted_at: this.convertDateToString(post.long_posted_at),
            calculated_time_update: calculateTimeSincePosted(post.smartcare_update_date),
            smartcare_update_date: this.convertDateToString(post.smartcare_update_date),
          }));
          this.requestStatus = 'success';
          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    openDialog() {
      this.dialog = true;
    },
    async encodeFilter(sac_call_id) {
      const newCardFilter = {
        sac_call_ids: {
          value: [
            sac_call_id,
          ],
        },
        pages: {
          value: this.activeDashboard.data.pages.map(page => page.id),
        },
        channels: {
          value: Object.values(this.channelsList),
        },
        sac_type: {
          value: Object.keys(this.sacTypeList),
        },
        is_sac: true,
      };
      const filterId = await WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });

      const dateRange = this.standardDateRange;
      const activatePanelURL = getWarroomURL(this.isStaging, this.accountId, this.universeId);
      const smartCareURL = getSmartCareURL(this.isStaging, dateRange, filterId);
      window.open(activatePanelURL, '_blank').location.replace(smartCareURL);
    },
    convertDateToString(date) {
      return new Date(date).toLocaleString('pt-br');
    },
    formatTimeSincePosted(time) {
      if (isNaN(time.minutesDifference) & isNaN(time.preciseHours)) {
        return '-';
      }
      if (time.minutesDifference < 60) {
        return `${time.minutesDifference} ${this.$t('components.OperatorsLogs.minute')}${time.minutesDifference !== 1 ? 's' : ''}`;
      }
      return `${time.preciseHours} ${this.$t('components.OperatorsLogs.hour')}${time.preciseHours !== 1 ? 's' : ''}`;
    },
  },
};
</script>

<style lang="scss" scoped>

.v-select__selection.v-select__selection--comma {
  line-height: 120%;
}

.internal-style {
  width: 100%;
  display: flex;
}

.buttons {
  display: grid;
}

.clean-sla-buttons {
  box-shadow: none;
  border-radius: unset;
}

.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

::v-deep {
  .table-truncate {
    td {
      max-width: 10vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }
}
</style>
